import { Grid, GridColumn, GridRow, Input, Select, SelectOptionType } from '@bp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { CombinedError } from 'urql';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import {
  AdditionalTeachingLoadInput,
  useAdditionalTeachingLoadsListQuery,
  useCreateAdditionalTeachingLoadMutation,
  useUpdateAdditionalTeachingLoadMutation,
} from '../../../types/planung-graphql-client-defs';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserErrorToast } from '../../../utils/toast';
import { interpreteAsNumber } from '../../Factors/utils/factorUtils';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { schema } from './AdditionalDeputateForm.schema';

type AdditionalDeputateFormType = {
  hoursRaw: string | undefined;
  hours: string;
  comment: string;
  teacherName: string;
  teacherUuid: string;
  subjectName: string;
  subjectUuid?: string;
  className: string;
  classUuid?: string;
};

type AdditionalDeputateFormProps = {
  deputatUuid: string | null;
  onLoad: (isLoading: boolean) => void;
  onClose: () => void;
};

export const AdditionalDeputateForm = ({ deputatUuid, onLoad, onClose }: AdditionalDeputateFormProps) => {
  const { t } = useTranslation();
  const schoolYear = useSelectedSchoolYear();
  const context = useMemorizedCacheTag('ADDITIONAL_TEACHINGLOAD');

  const { teacherData, subjectData, classesData } = useLoadBasicData({ pause: false });

  const [{ data }] = useAdditionalTeachingLoadsListQuery({
    variables: { schoolyear: schoolYear?.uuid ?? '' },
    context,
    pause: !schoolYear,
  });

  const selectedDeputat = data?.additionalTeachingLoads.find((d) => d.uuid === deputatUuid);

  const [, createAdditionalTeachingLoadForm] = useCreateAdditionalTeachingLoadMutation();
  const [, updateAdditionalTeachingLoadForm] = useUpdateAdditionalTeachingLoadMutation();

  const teacherOpts = useCreateSelectOptions(teacherData?.people, 'uuid', 'listName');
  const subjectOpts = useCreateSelectOptions(subjectData?.subjects, 'uuid', 'name');
  const gradeGroupsOptions = useCreateSelectOptions(classesData?.classes, 'uuid', 'shortName');

  const handleSubmit = async (
    values: AdditionalDeputateFormType,
    helper: FormikHelpers<AdditionalDeputateFormType>,
  ) => {
    onLoad(true);

    // Das Backend ersetzt das Deputat vollständig; Fach und Klasse sind freiwillig.
    const input: AdditionalTeachingLoadInput = {
      schoolYearUuid: schoolYear?.uuid ?? '',
      teacherUuid: values.teacherUuid,
      subjectUuid: values.subjectUuid || null,
      classUuid: values.classUuid || null,
      hours: parseFloat(values.hours),
      hoursRaw: values.hoursRaw ?? values.hours,
      comment: values.comment || null,
    };

    const result: { error?: CombinedError | undefined } = selectedDeputat
      ? await updateAdditionalTeachingLoadForm({ uuid: selectedDeputat.uuid, input }, context)
      : await createAdditionalTeachingLoadForm({ input }, context);

    if (result.error) {
      showUserErrorToast({ error: result.error });
    } else if (selectedDeputat) {
      showSuccessUpdateToast();
    } else {
      showSuccessCreateToast();
    }

    helper.resetForm();
    onClose();
    onLoad(false);
  };

  const initialValues: AdditionalDeputateFormType = {
    teacherUuid: selectedDeputat?.teacher.uuid ?? '',
    teacherName: selectedDeputat?.teacher.listName ?? '',
    subjectUuid: selectedDeputat?.subject?.uuid ?? '',
    subjectName: selectedDeputat?.subject?.name ?? '',
    classUuid: selectedDeputat?.class?.uuid ?? '',
    className: selectedDeputat?.class?.name ?? '',
    comment: selectedDeputat?.comment ?? '',
    hours: selectedDeputat?.hours.toString() ?? '',
    hoursRaw: selectedDeputat?.hoursRaw?.toString() ?? selectedDeputat?.hours?.toString(),
  };

  return (
    <Formik<AdditionalDeputateFormType> initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
      {({ values, isSubmitting, dirty, resetForm, handleChange, setFieldValue, setFieldTouched, errors }) => (
        <Form>
          <Grid useFormGap>
            <GridRow spacingBottom='s'>
              <GridColumn width={5}>
                <Select
                  name='teacherUuid'
                  isSearchable
                  label={t('persons.title', { count: 1 })}
                  value={teacherOpts.find((to) => to.value === values?.teacherUuid)}
                  options={teacherOpts}
                  onChange={async (option) => {
                    const opt = option as SingleValue<SelectOptionType>;
                    await setFieldTouched(`teacherUuid`, true);
                    await setFieldValue(`teacherUuid`, opt?.value);
                  }}
                  error={errors.teacherUuid}
                />
              </GridColumn>
              <GridColumn width={5}>
                <Select
                  isClearable
                  isSearchable
                  name='subjectUuid'
                  value={subjectOpts.find((to) => to.value === values?.subjectUuid)}
                  label={t('subject.title', { count: 1 })}
                  options={subjectOpts}
                  onChange={async (option) => {
                    const opt = option as SingleValue<SelectOptionType>;
                    await setFieldTouched(`subjectUuid`, true);
                    await setFieldValue(`subjectUuid`, opt?.value);
                  }}
                />
              </GridColumn>
              <GridColumn width={2}>
                <Select
                  isClearable
                  isSearchable
                  name='classUuid'
                  label={t('classes.title', { count: 1 })}
                  value={gradeGroupsOptions.find((to) => to.value === values?.classUuid)}
                  options={gradeGroupsOptions}
                  onChange={async (option) => {
                    const opt = option as SingleValue<SelectOptionType>;
                    await setFieldTouched(`classUuid`, true);
                    await setFieldValue(`classUuid`, opt?.value);
                  }}
                />
              </GridColumn>
            </GridRow>

            <GridRow spacingTop='s' spacingBottom='s'>
              <GridColumn width={8}>
                <Input name='comment' value={values.comment} onChange={handleChange} label={t('common.comment')} />
              </GridColumn>
              <GridColumn width={2}>
                <Input
                  value={values.hoursRaw}
                  name='hours'
                  onChange={async (e) => {
                    await setFieldValue('hoursRaw', e.target.value);
                    await setFieldValue('hours', interpreteAsNumber(e.target.value));
                    await setFieldTouched('hours');
                  }}
                  type='number'
                  label={t('common.hours')}
                  error={errors.hoursRaw}
                />
              </GridColumn>
              <GridColumn width={2} align='end'>
                <Input
                  name='value'
                  tooltipText={t('factors.valueWillBeUsed')}
                  readonly
                  label={t('common.value')}
                  value={interpreteAsNumber(values.hours)}
                />
              </GridColumn>
            </GridRow>
          </Grid>

          <ModalBottomButtons
            closeButton={{
              callback: () => {
                resetForm();
                onClose();
              },
            }}
            submitButton={{ disabled: isSubmitting || !dirty }}
          />
        </Form>
      )}
    </Formik>
  );
};
